import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "@tanstack/react-router";
import { useMemo } from "react";
import { generateSchemaMarkup, SchemaConfig } from "@/utils/schema-generators";
import { SupportedLanguage } from "@/config/config";
// SEO Constants
const SEO_CONSTANTS = {
  DOMAIN: "https://www.efb.eg",
  TWITTER_HANDLE: "@EGYFOODBANK",
  DEFAULT_IMAGE: "https://minio-coh.efb.eg/efb/documents/COH.svg",
  MAX_TITLE_LENGTH: 65,
  MAX_DESCRIPTION_LENGTH: 156,
  LOCALES: {
    ar: "ar_EG",
    en: "en_US",
  } as const,
} as const;

interface SEOHeadProps {
  pageKey: string;
  children?: React.ReactNode;
  // Optional overrides for dynamic content
  title?: string;
  description?: string;
  image?: string;
  ogType?: "website" | "article";
  // Optional dynamic data for schema (e.g., FAQs, dates, etc.)
  dynamicData?: Record<string, any>;
}

/**
 * Simple SEOHead component that reads SEO data from translation files
 */
export function SEOHead({
  pageKey,
  children,
  title,
  description,
  image,
  ogType,
  dynamicData,
}: SEOHeadProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const seoData = useMemo(() => {
    const siteName = i18n.language === "ar" ? "بنك الطعام المصري" : "Egyptian Food Bank";
    const locale = SEO_CONSTANTS.LOCALES[i18n.language as keyof typeof SEO_CONSTANTS.LOCALES];

    // Get SEO data from translations
    const seoTitle = title || t(`seo.${pageKey}.title`, { defaultValue: siteName });
    const seoDescription =
      description?.replaceAll(/<[^>]+>/g, "") ||
      t(`seo.${pageKey}.description`, { defaultValue: "" });
    const seoKeywords = t(`seo.${pageKey}.keywords`, { defaultValue: "" });
    // Use title/description as fallback for OG if not provided separately
    const ogTitle = t(`seo.${pageKey}.ogTitle`, { defaultValue: seoTitle });
    const ogDescription = t(`seo.${pageKey}.ogDescription`, { defaultValue: seoDescription });

    // Build canonical URL
    const canonicalUrl = buildCanonicalUrl(location.pathname, location.search);

    // Determine image
    const ogImage = image || SEO_CONSTANTS.DEFAULT_IMAGE;

    // Determine og:type
    const finalOgType = ogType || (isArticlePage(pageKey) ? "article" : "website");

    // Generate schema markup
    const schemaConfig: SchemaConfig = {
      pageKey,
      language: i18n.language as SupportedLanguage,
      url: canonicalUrl,
      title: seoTitle,
      description: seoDescription,
      image: ogImage,
      dynamicData,
    };
    const schemaMarkup = generateSchemaMarkup(schemaConfig);

    return {
      title: truncateText(seoTitle, SEO_CONSTANTS.MAX_TITLE_LENGTH),
      description: truncateText(seoDescription, SEO_CONSTANTS.MAX_DESCRIPTION_LENGTH),
      keywords: seoKeywords || undefined,
      canonicalUrl,
      ogTitle: truncateText(ogTitle, SEO_CONSTANTS.MAX_TITLE_LENGTH),
      ogDescription: truncateText(ogDescription, SEO_CONSTANTS.MAX_DESCRIPTION_LENGTH),
      ogImage,
      ogLocale: locale,
      ogUrl: canonicalUrl,
      ogSiteName: siteName,
      ogType: finalOgType,
      twitterCard: "summary_large_image" as const,
      twitterSite: SEO_CONSTANTS.TWITTER_HANDLE,
      twitterTitle: truncateText(ogTitle, SEO_CONSTANTS.MAX_TITLE_LENGTH),
      twitterDescription: truncateText(ogDescription, SEO_CONSTANTS.MAX_DESCRIPTION_LENGTH),
      twitterImage: ogImage,
      schemaMarkup,
    };
  }, [
    pageKey,
    title,
    description,
    image,
    ogType,
    dynamicData,
    t,
    i18n.language,
    location.pathname,
    location.search,
  ]);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoData.title}</title>
      <meta name="description" content={seoData.description} />
      {seoData.keywords && <meta name="keywords" content={seoData.keywords} />}

      {/* Canonical URL */}
      <link rel="canonical" href={seoData.canonicalUrl} />

      {/* Open Graph Tags */}
      <meta property="og:title" content={seoData.ogTitle} />
      <meta property="og:description" content={seoData.ogDescription} />
      <meta property="og:image" content={seoData.ogImage} />
      <meta property="og:locale" content={seoData.ogLocale} />
      <meta property="og:url" content={seoData.ogUrl} />
      <meta property="og:site_name" content={seoData.ogSiteName} />
      <meta property="og:type" content={seoData.ogType} />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={seoData.twitterCard} />
      <meta name="twitter:site" content={seoData.twitterSite} />
      <meta name="twitter:title" content={seoData.twitterTitle} />
      <meta name="twitter:description" content={seoData.twitterDescription} />
      <meta name="twitter:image" content={seoData.twitterImage} />

      {/* Schema Markup */}
      <script type="application/ld+json">{seoData.schemaMarkup}</script>

      {/* Additional custom meta tags */}
      {children}
    </Helmet>
  );
}

/**
 * Builds canonical URL by removing query parameters except pagination
 */
function buildCanonicalUrl(pathname: string, search?: Record<string, any>): string {
  const url = new URL(pathname, SEO_CONSTANTS.DOMAIN);

  // Only preserve pagination query parameters
  if (search) {
    const allowedParams = ["page"];
    Object.entries(search).forEach(([key, value]) => {
      if (allowedParams.includes(key) && value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    });
  }

  return url.toString();
}

/**
 * Truncates text to specified length while preserving word boundaries
 */
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;

  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");

  if (lastSpace > maxLength * 0.8) {
    return truncated.substring(0, lastSpace) + "...";
  }

  return truncated.substring(0, maxLength - 3) + "...";
}

/**
 * Determines if a page should use article schema
 */
function isArticlePage(pageKey: string): boolean {
  return ["blog-detail", "fatwa-detail", "program-detail"].includes(pageKey);
}
